import { X } from "@tamagui/lucide-icons";
import { Platform, View } from "react-native";
import { ScrollView, XStack, YStack } from "tamagui";

import { AttachmentType } from "@/api/mutations/conversation";
import LoadingSpinner from "@/components/animated/LoadingSpinner";
import { Button } from "@/components/Button";
import Image from "@/components/Image";
import ImageGallery from "@/components/ImageGallery";
import { Text } from "@/components/Typography";
import { useImageGalleryStore } from "@/store";
import { getFriendlyFileType, isHeic } from "@/utils/attachments";

interface AttachmentPreviewProps {
  attachments: AttachmentType[];
  onRemoveAttachment: (localUri: string) => void;
}

const AttachmentPreview = ({
  attachments,
  onRemoveAttachment,
}: AttachmentPreviewProps) => {
  const { openImageGallery } = useImageGalleryStore();

  if (attachments.length === 0) {
    return null;
  }

  const imageAttachments = attachments.filter((a) => a.isImage);
  const imageUris = imageAttachments.map((a) => a.presignedUrl ?? a.localUri);

  return (
    <>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          padding: "$150",
          paddingBottom: "$100",
        }}
      >
        <XStack gap="$150">
          {attachments.map(
            ({
              localUri,
              attachmentId,
              name,
              mimeType,
              isImage,
              presignedUrl,
            }) => {
              return (
                <YStack key={localUri} position="relative">
                  {isImage ? (
                    <Button.Wrapper
                      onPress={() =>
                        openImageGallery(imageUris, presignedUrl ?? localUri)
                      }
                      aria-label="Selected image, tap to view"
                    >
                      {presignedUrl || !isHeic(mimeType) ? (
                        <Image
                          source={{ uri: presignedUrl ?? localUri }}
                          width="$1000"
                          height="$1000"
                          borderRadius="$100"
                        />
                      ) : (
                        <View
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: 8,
                          }}
                        />
                      )}
                    </Button.Wrapper>
                  ) : (
                    <Button.Wrapper
                      dataTrackingId="user-uploaded-file"
                      onPress={() => {
                        if (Platform.OS === "web" && presignedUrl) {
                          window.open(presignedUrl, "_blank");
                        }
                      }}
                      disabled={!presignedUrl}
                      aria-label={name}
                    >
                      <YStack
                        width="$1000"
                        height="$1000"
                        borderRadius="$150"
                        borderColor="$strokeSubdued"
                        borderWidth="$strokeWidth"
                        alignItems="center"
                        justifyContent="center"
                        padding="$150"
                        opacity={!presignedUrl ? 0.5 : 1}
                      >
                        <Text
                          color="$textPrimary"
                          fontSize="$2xs"
                          lineHeight="$xs"
                          bold
                          numberOfLines={2}
                          ellipsizeMode="tail"
                          width="100%"
                          marginTop="auto"
                        >
                          {name}
                        </Text>
                        <Text
                          color="$textSecondary"
                          fontSize="$2xs"
                          lineHeight="$xs"
                          numberOfLines={1}
                          ellipsizeMode="middle"
                          alignSelf="flex-start"
                          marginTop="auto"
                        >
                          {getFriendlyFileType(mimeType)}
                        </Text>
                      </YStack>
                    </Button.Wrapper>
                  )}
                  {!attachmentId && (
                    <YStack
                      position="absolute"
                      width="100%"
                      height="100%"
                      backgroundColor="$overlay"
                      borderRadius="$100"
                      justifyContent="center"
                      alignItems="center"
                      pointerEvents="none"
                    >
                      <LoadingSpinner />
                    </YStack>
                  )}
                  <Button.Wrapper
                    position="absolute"
                    top="$-50"
                    right="$-50"
                    backgroundColor="$fillButtonNeutral"
                    padding="$50"
                    onPress={() => onRemoveAttachment(localUri)}
                    aria-label="Remove attachment"
                  >
                    <X size="$200" color="$iconCta" flexShrink={0} />
                  </Button.Wrapper>
                </YStack>
              );
            },
          )}
        </XStack>
      </ScrollView>
      <ImageGallery />
    </>
  );
};

export default AttachmentPreview;
